// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { Routes, Route, Navigate } from 'react-router-dom'
import ChatPage from './ChatPage'
import BoardPage from './board/BoardPage'
import AdminDashboard from './admin/AdminDashboard'
import UserManagement from './admin/UserManagement'
import GroupManagement from './admin/GroupManagement'
import AgentSettings from './admin/AgentSettings'
import DailyJourney from './admin/DailyJourney'
import FeedbackViewer from './admin/FeedbackViewer'
import UsageDashboard from './admin/UsageDashboard'
import UsageLimits from './admin/UsageLimits'
import AuditLogViewer from './admin/AuditLogViewer'
import KnowledgeManagement from './admin/KnowledgeManagement'
import { AdminLayout } from '@/components/admin/AdminLayout'
import { RoleGuard } from '@/components/auth/RoleGuard'

// 管理メニューの出し分け（2026-06-14）:
//   入口（/admin）は認証済み全員に開く。中身はロール別。
//   - member / viewer: AIアシスタント設定（/admin/agent）だけ。index は /admin/agent へ着地。
//   - admin: 管理一式（ダッシュボード・利用状況・グループ・ナレッジ・監査ログ・ユーザー一覧）。
//   - owner: ＋ユーザーのロール編集（編集 UI は UserManagement 内で owner 限定・API も owner 強制）。
//   ロール/ユーザー管理は Cognito グループ基準の UserManagement に一本化。旧 MemberManagement
//   （membership 基準）は畳み、/admin/members は /admin/users へリダイレクト。
export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<ChatPage />} />
      <Route path="/board" element={<BoardPage />} />
      {/* 入口は全員（AuthProvider 配下＝認証済み）。各ページの権限は子ルートで強制。 */}
      <Route path="/admin" element={<AdminLayout />}>
        {/* ランディング: admin 以上はダッシュボード、member/viewer は AIアシスタント設定へ。 */}
        <Route
          index
          element={
            <RoleGuard requiredRole="admin" redirectTo="/admin/agent">
              <AdminDashboard />
            </RoleGuard>
          }
        />
        {/* AIアシスタント設定: 認証済み全員（viewer 以上＝実質全員）が自分の相棒を設定可。 */}
        <Route path="agent" element={<AgentSettings />} />
        {/* これまでの歩み: 自分の日誌（Daily md）の閲覧。全員（本人の分のみ・API 側で sub 強制） */}
        <Route path="journey" element={<DailyJourney />} />
        {/* 以下は admin 以上のみ。member/viewer が直叩きしてもチャットへ戻す。 */}
        <Route
          path="groups"
          element={
            <RoleGuard requiredRole="admin">
              <GroupManagement />
            </RoleGuard>
          }
        />
        {/* ナレッジ管理（v2）: KnowledgeDialog asPage をフルページで表示。
            viewer 以上に開放（自分の personal ナレッジを管理できるため・サーバ側で scope 権限を強制）。 */}
        <Route path="knowledge" element={<KnowledgeManagement />} />
        <Route
          path="usage"
          element={
            <RoleGuard requiredRole="admin">
              <UsageDashboard />
            </RoleGuard>
          }
        />
        {/* 利用上限の編集は owner 限定（サイドバー minRole=owner ＋ API も owner 強制）。 */}
        <Route
          path="usage-limits"
          element={
            <RoleGuard requiredRole="owner">
              <UsageLimits />
            </RoleGuard>
          }
        />
        <Route
          path="audit"
          element={
            <RoleGuard requiredRole="admin">
              <AuditLogViewer />
            </RoleGuard>
          }
        />
        {/* フィードバック閲覧: 👍👎の一覧（admin 以上・API 側でも require_role admin） */}
        <Route
          path="feedback"
          element={
            <RoleGuard requiredRole="admin">
              <FeedbackViewer />
            </RoleGuard>
          }
        />
        {/* ユーザー/ロール一覧: admin も閲覧可（読み取り）。ロール編集 UI は UserManagement 内で
            owner 限定・ロール変更 API も admin Lambda が owner を強制する。 */}
        <Route
          path="users"
          element={
            <RoleGuard requiredRole="admin">
              <UserManagement />
            </RoleGuard>
          }
        />
        {/* 旧・メンバー管理は Cognito 基準のユーザー画面へ一本化（重複排除）。 */}
        <Route path="members" element={<Navigate to="/admin/users" replace />} />
      </Route>
    </Routes>
  )
}
